import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { z } from 'zod';
import { orpc } from '@/api/rpc/client';
import {
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useCreateEmailTemplateDraft,
  useUpdateEmailTemplateDraft,
  useFinalizeEmailTemplateDraft,
  usePublishEmailTemplateVersion,
} from '@/api/rpc/hooks/communications/useEmailTemplates';
import type { EmailTemplateResponse } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  PlusIcon,
  TrashIcon,
  CopyIcon,
  RocketLaunchIcon,
  CheckIcon,
  EnvelopeSimpleIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { HtmlWYSIWYGEditor } from '@/components/common/HtmlWYSIWYGEditor';
import type { EmailTemplateType } from '@erp/shared-types';
import {
  EMPTY_JSON_SPEC,
  parseEmailSpec,
  parseJsonRecord,
  stringifyJsonSpec,
} from './emailTemplateJsonSpec';
import {
  DEFAULT_SAMPLE_VARIABLES,
  JsonTemplateEditor,
} from './JsonTemplateEditor';
import { parsePartialProposalSpec } from './partialEmailSpec';
import { useToast } from '@/context/ToastContext';
import { useAgentModule } from '@/hooks/useAgentModule';
import { useAgentState } from '@/context/agent-state.context';
import type { Message, MessageToolResult } from '@/context/agent-state.context';

interface EmailTemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  template?: EmailTemplateResponse;
}

interface EmailTemplateFormProps {
  template?: EmailTemplateResponse;
  draftVersionId?: string | null;
  onCancel: () => void;
  onSaved: () => void;
  expandedJsonEditor?: boolean;
  readOnly?: boolean;
}

interface VariableEntry {
  key: string;
  label: string;
  /** Realistic preview value (built-ins ship these); seeds the preview state. */
  sampleValue?: unknown;
}

/**
 * Preview JSON seeded from the template's own variable samples, so the live
 * preview renders the template's real shape (detail-row repeats, CTA
 * visibility gates) instead of the generic invoice sample — with only the
 * invoice default, data-driven templates like "Order review required" render
 * an apparently empty body. Null when no variable carries a sample.
 */
function sampleVariablesFromTemplate(
  variables: VariableEntry[] | undefined,
): string | null {
  const entries = (variables ?? []).filter((v) => v.sampleValue !== undefined);
  if (entries.length === 0) return null;
  return JSON.stringify(
    Object.fromEntries(entries.map((v) => [v.key, v.sampleValue])),
    null,
    2,
  );
}

// Tool results arrive untyped from the agent stream — validate at runtime
// instead of trusting `as` casts.
const ProposalToolResultSchema = z.object({
  ok: z.literal(true),
  jsonSpec: z.record(z.string(), z.unknown()),
  sampleVariables: z.record(z.string(), z.unknown()),
  subject: z.string().nullish(),
  templateName: z.string().nullish(),
  summary: z.string().optional(),
});

const CreateBlockToolResultSchema = z.object({
  ok: z.literal(true),
  name: z.string().optional(),
});

export function EmailTemplateFormDialog({
  open,
  onClose,
  template,
}: EmailTemplateFormDialogProps) {
  const isEditMode = Boolean(template);

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? (
              <T>Edit Email Template</T>
            ) : (
              <T>Create Email Template</T>
            )}
          </DialogTitle>
        </DialogHeader>
        <EmailTemplateForm
          template={template}
          onCancel={onClose}
          onSaved={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

export function EmailTemplateForm({
  template,
  draftVersionId,
  onCancel,
  onSaved,
  expandedJsonEditor = false,
  readOnly = false,
}: EmailTemplateFormProps) {
  const isEditMode = Boolean(template);
  const gt = useGT();
  const { showToast } = useToast();

  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(
    template?.textContent ?? template?.htmlContent ?? '',
  );
  const [templateType, setTemplateType] = useState<EmailTemplateType>(
    template?.templateType ?? 'html',
  );
  const [jsonSpec, setJsonSpec] = useState(
    stringifyJsonSpec(template?.jsonSpec),
  );
  const [sampleVariables, setSampleVariables] = useState(
    () =>
      sampleVariablesFromTemplate(template?.variables) ??
      DEFAULT_SAMPLE_VARIABLES,
  );
  const [variables, setVariables] = useState<VariableEntry[]>(
    template?.variables ?? [],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');

  // When set, a save is pending confirmation because the template type was
  // switched (HTML ⇄ Template Blocks), which drops the other format's content.
  const [pendingSaveKind, setPendingSaveKind] = useState<
    'draft' | 'publish' | null
  >(null);

  // Refs so the auto-apply listener always sees the latest values without
  // needing to re-register on every render.
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const nameRef = useRef(name);
  nameRef.current = name;

  const { registerToolResultListener, registerToolInputStreamListener } =
    useAgentState();
  const queryClient = useQueryClient();

  // Auto-apply proposals: when the agent calls propose_email_template_spec,
  // write the spec (and sample variables / subject) into the editor. The
  // editor's debounced preview effect re-renders automatically. Proposals
  // are always Template Blocks specs, so applying one to an HTML template
  // switches it to json_render.
  useEffect(() => {
    return registerToolResultListener((_toolCallId, toolName, result) => {
      // The agent creates blocks server-side, bypassing the mutation hooks
      // that normally invalidate caches — refresh them so the template
      // blocks pages don't show stale data.
      if (toolName === 'create_template_block') {
        queryClient.invalidateQueries({
          queryKey: orpc.communications.templateBlocks.list.queryKey({
            input: {},
          }),
        });
        queryClient.invalidateQueries({
          queryKey: orpc.communications.templateBlocks.listVersions.key(),
        });
        return;
      }

      if (toolName !== 'propose_email_template_spec') return;
      if (readOnlyRef.current) return;
      const parsed = ProposalToolResultSchema.safeParse(result);
      if (!parsed.success) return;
      const r = parsed.data;
      setTemplateType('json_render');
      setJsonSpec(JSON.stringify(r.jsonSpec, null, 2));
      setSampleVariables(JSON.stringify(r.sampleVariables, null, 2));
      if (typeof r.subject === 'string' && r.subject.trim()) {
        setSubject(r.subject);
      }
      // Only set the name when it's still blank, so we never clobber a
      // user-entered or existing template name.
      if (
        typeof r.templateName === 'string' &&
        r.templateName.trim() &&
        !nameRef.current.trim()
      ) {
        setName(r.templateName);
      }
    });
  }, [queryClient, registerToolResultListener]);

  // Progressive streaming: as the agent writes the propose_email_template_spec
  // tool input, parse the accumulating partial JSON into a renderable (pruned)
  // spec and write it into the editor so the debounced preview grows in real
  // time. The final tool-result (handled above) is the source of truth and
  // overwrites whatever partial state was applied.
  useEffect(() => {
    return registerToolInputStreamListener(
      (_toolCallId, toolName, partialInput) => {
        if (toolName !== 'propose_email_template_spec') return;
        if (readOnlyRef.current) return;
        const partialSpec = parsePartialProposalSpec(partialInput);
        if (!partialSpec) return;
        setTemplateType('json_render');
        setJsonSpec(JSON.stringify(partialSpec, null, 2));
      },
    );
  }, [registerToolInputStreamListener]);

  const renderToolResult = useCallback(
    (_msg: Message, toolResult: MessageToolResult) => {
      if (toolResult.toolName === 'create_template_block') {
        const parsed = CreateBlockToolResultSchema.safeParse(toolResult.result);
        if (!parsed.success) return null;
        return (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600">
            <CheckIcon className="h-3 w-3 flex-shrink-0" />
            <span>
              {parsed.data.name
                ? gt('Created block "{name}".', { name: parsed.data.name })
                : gt('Created block.')}
            </span>
          </div>
        );
      }
      if (toolResult.toolName !== 'propose_email_template_spec') return null;
      const parsed = ProposalToolResultSchema.safeParse(toolResult.result);
      if (!parsed.success) return null;
      return (
        <div className="flex items-center gap-1.5 text-xs text-emerald-600">
          <CheckIcon className="h-3 w-3 flex-shrink-0" />
          <span>{parsed.data.summary ?? gt('Spec applied.')}</span>
        </div>
      );
    },
    [gt],
  );

  useAgentModule({
    moduleKey: 'email-templates',
    currentPage: name.trim() || 'Email Template',
    getModuleContext: () => {
      const specResult = parseEmailSpec(jsonSpec);
      const variablesResult = parseJsonRecord(sampleVariables);
      return {
        templateName: name || null,
        subject: subject || null,
        templateType,
        htmlContent: templateType === 'html' ? body : null,
        jsonSpec:
          templateType === 'json_render' ? (specResult.data ?? null) : null,
        specError:
          templateType === 'json_render' ? (specResult.error ?? null) : null,
        sampleVariables: variablesResult.data ?? null,
      };
    },
    renderToolResult,
    placeholder: 'Describe the email template you want to build…',
    headerTitle: 'Email Builder',
    headerIcon: (
      <EnvelopeSimpleIcon size={18} weight="duotone" className="text-sky-500" />
    ),
  });

  const undefinedVars = useMemo(() => {
    if (templateType === 'json_render') return [];
    const definedKeys = new Set(variables.map((v) => v.key));
    const referenced = new Set<string>();
    const pattern = /\{\{(\w+)\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(subject)) !== null) referenced.add(match[1]);
    while ((match = pattern.exec(body)) !== null) referenced.add(match[1]);
    return [...referenced].filter((key) => !definedKeys.has(key));
  }, [subject, body, templateType, variables]);

  function resetForm() {
    setName('');
    setSubject('');
    setBody('');
    setTemplateType('html');
    setJsonSpec(EMPTY_JSON_SPEC);
    setSampleVariables(DEFAULT_SAMPLE_VARIABLES);
    setVariables([]);
    setErrors({});
    setNewVarKey('');
    setNewVarLabel('');
  }

  const handleBodyChange = useCallback((content: string) => {
    setBody(content);
  }, []);

  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const createDraft = useCreateEmailTemplateDraft();
  const updateDraft = useUpdateEmailTemplateDraft();
  const finalizeDraft = useFinalizeEmailTemplateDraft();
  const publishVersion = usePublishEmailTemplateVersion();

  function addVariable() {
    const key = newVarKey.trim();
    const label = newVarLabel.trim() || key;
    if (!key) return;
    if (!/^\w+$/.test(key)) {
      setErrors((prev) => ({
        ...prev,
        variable: gt('Key must be alphanumeric with underscores only', {
          context: 'email template variable key validation error',
        }),
      }));
      return;
    }
    if (variables.some((v) => v.key === key)) {
      setErrors((prev) => ({
        ...prev,
        variable: gt('Variable key already exists', {
          context: 'email template variable key duplicate error',
        }),
      }));
      return;
    }
    setVariables((prev) => [...prev, { key, label }]);
    setNewVarKey('');
    setNewVarLabel('');
    setErrors((prev) => {
      const { variable: _, ...rest } = prev;
      return rest;
    });
  }

  function removeVariable(key: string) {
    setVariables((prev) => prev.filter((v) => v.key !== key));
  }

  function insertVariable(key: string) {
    void navigator.clipboard.writeText(`{{${key}}}`);
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};
    if (!name.trim())
      newErrors.name = gt('Name is required', {
        context: 'email template form validation',
      });
    if (!subject.trim())
      newErrors.subject = gt('Subject is required', {
        context: 'email template form validation',
      });
    if (templateType === 'html' && !body.trim())
      newErrors.body = gt('Body is required', {
        context: 'email template form validation',
      });
    if (templateType === 'json_render') {
      const parsedJsonSpec = parseEmailSpec(jsonSpec);
      if (!jsonSpec.trim()) {
        newErrors.jsonSpec = gt('Template blocks spec is required', {
          context: 'email template form validation',
        });
      } else if (parsedJsonSpec.error) {
        newErrors.jsonSpec = parsedJsonSpec.error;
      }
    }
    if (undefinedVars.length > 0) {
      newErrors.undefinedVars = `${gt('Undefined variables:', { context: 'email template undefined variable warning' })} ${undefinedVars.map((k) => `{{${k}}}`).join(', ')}`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleClose() {
    resetForm();
    onCancel();
  }

  function getFormPayload() {
    const parsedJsonSpec =
      templateType === 'json_render' ? parseEmailSpec(jsonSpec).data : null;
    const bodyValue =
      templateType === 'json_render' ? jsonSpec.trim() : body.trim();
    return { parsedJsonSpec, bodyValue };
  }

  function handleSubmit() {
    if (!validateForm()) return;
    const { parsedJsonSpec, bodyValue } = getFormPayload();

    const onSuccess = () => {
      resetForm();
      onSaved();
    };
    const onError = (error: Error) => {
      setErrors({
        form:
          error.message ||
          gt('Failed to save template', {
            context: 'email template form error',
          }),
      });
    };

    if (isEditMode && template) {
      updateTemplate.mutate(
        {
          id: template.id,
          name: name.trim(),
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        },
        { onSuccess, onError },
      );
    } else {
      createTemplate.mutate(
        {
          name: name.trim(),
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        },
        { onSuccess, onError },
      );
    }
  }

  // The template name lives on the lineage, not on a version, so the draft /
  // publish flow (which only writes versions) doesn't carry it. Persist a
  // rename separately. `update` with only { id, name } updates the lineage
  // without creating a new version.
  async function persistNameIfChanged() {
    if (!template) return;
    const trimmed = name.trim();
    if (trimmed && trimmed !== template.name) {
      await updateTemplate.mutateAsync({ id: template.id, name: trimmed });
    }
  }

  async function handleSaveDraft() {
    if (!validateForm() || !template) return;
    const { parsedJsonSpec, bodyValue } = getFormPayload();
    try {
      await persistNameIfChanged();
      if (draftVersionId) {
        await updateDraft.mutateAsync({
          versionId: draftVersionId,
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        });
      } else {
        await createDraft.mutateAsync({
          emailTemplateId: template.id,
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        });
      }
      showToast({
        title: gt('Draft saved'),
        message: gt('Your changes have been saved as a draft.'),
        color: 'success',
      });
      onSaved();
    } catch (err) {
      setErrors({
        form: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function handleSaveAndPublish() {
    if (!validateForm() || !template) return;
    const { parsedJsonSpec, bodyValue } = getFormPayload();
    try {
      await persistNameIfChanged();
      let versionId: string;
      if (draftVersionId) {
        await updateDraft.mutateAsync({
          versionId: draftVersionId,
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        });
        versionId = draftVersionId;
      } else {
        const { version } = await createDraft.mutateAsync({
          emailTemplateId: template.id,
          subject: subject.trim(),
          body: bodyValue,
          variables,
          templateType,
          jsonSpec: parsedJsonSpec,
        });
        versionId = version.id;
      }
      const { version: finalized } = await finalizeDraft.mutateAsync({
        versionId,
      });
      await publishVersion.mutateAsync({
        emailTemplateId: template.id,
        versionId: finalized.id,
      });
      showToast({
        title: gt('Published'),
        message: gt('Your changes are now live.'),
        color: 'success',
      });
      resetForm();
      onSaved();
    } catch (err) {
      setErrors({
        form: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const isPending =
    createTemplate.isPending ||
    updateTemplate.isPending ||
    createDraft.isPending ||
    updateDraft.isPending ||
    finalizeDraft.isPending ||
    publishVersion.isPending;
  const isJsonRender = templateType === 'json_render';

  // True once the editor's type differs from the template's saved type — saving
  // now would drop the other format's content, so we confirm first.
  const isTypeSwitched =
    isEditMode && !!template && templateType !== template.templateType;

  function requestSave(kind: 'draft' | 'publish') {
    if (isTypeSwitched) {
      setPendingSaveKind(kind);
      return;
    }
    if (kind === 'draft') void handleSaveDraft();
    else void handleSaveAndPublish();
  }

  function confirmPendingSave() {
    const kind = pendingSaveKind;
    setPendingSaveKind(null);
    if (kind === 'draft') void handleSaveDraft();
    else if (kind === 'publish') void handleSaveAndPublish();
  }

  return (
    <>
      {/* The full-page editor supplies its own page padding (p-3) and threads
          a flex column down so the Template Blocks split can fill the pane;
          the extra vertical padding is only for the dialog context. */}
      <div
        className={cn(
          expandedJsonEditor
            ? 'flex min-h-0 flex-1 flex-col gap-4'
            : 'space-y-4 py-4',
        )}
      >
        {errors.form && (
          <p className="text-sm text-destructive">{errors.form}</p>
        )}

        <div className="flex flex-col gap-3 border-b pb-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <Input
              aria-label={gt('Template name', {
                context: 'email template inline title input aria label',
              })}
              placeholder={gt('Untitled email template', {
                context: 'email template inline title placeholder',
              })}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={readOnly}
              className="h-auto border-transparent bg-transparent px-0 py-1 text-2xl font-semibold tracking-tight shadow-none focus-visible:ring-0 md:text-3xl"
            />
            {errors.name ? (
              <p className="text-sm text-destructive">{errors.name}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                <T>
                  A unique identifier used to reference this template in code
                </T>
              </p>
            )}
          </div>

          <div
            role="radiogroup"
            aria-label={gt('Template type', {
              context: 'email template type segmented control aria label',
            })}
            className="inline-flex shrink-0 rounded-md border bg-muted p-1"
          >
            <button
              type="button"
              role="radio"
              aria-checked={templateType === 'html'}
              onClick={() => !readOnly && setTemplateType('html')}
              disabled={readOnly}
              className={cn(
                'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                templateType === 'html'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <T context="email template type option">HTML</T>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={templateType === 'json_render'}
              onClick={() => !readOnly && setTemplateType('json_render')}
              disabled={readOnly}
              className={cn(
                'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                templateType === 'json_render'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <T context="email template type option">Template Blocks</T>
            </button>
          </div>
        </div>

        {!isJsonRender && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>
                <T>Template Variables</T>
              </Label>
              <span className="text-xs text-muted-foreground">
                <T context="instruction to use template variable syntax">
                  {'Use {{key}} in subject and body'}
                </T>
              </span>
            </div>

            {variables.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {variables.map((v) => (
                  <Badge
                    key={v.key}
                    variant="secondary"
                    className={cn(
                      'gap-1.5 pl-2.5 pr-1 py-1 cursor-pointer group',
                      'hover:bg-secondary/80',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      className="flex items-center gap-1.5"
                      title={gt('Insert {variable} into body', {
                        context:
                          'insert variable into email template body button',
                        variable: `{{${v.key}}}`,
                      })}
                    >
                      <code className="text-xs font-mono">{`{{${v.key}}}`}</code>
                      {v.label !== v.key && (
                        <span className="text-xs text-muted-foreground">
                          {v.label}
                        </span>
                      )}
                      <CopyIcon className="h-3 w-3 opacity-0 group-hover:opacity-60" />
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeVariable(v.key)}
                        className="ml-1 rounded-sm p-0.5 hover:bg-destructive/20 hover:text-destructive"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}

            {!readOnly && (
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="newVarKey" className="text-xs">
                    <T>Key</T>
                  </Label>
                  <Input
                    id="newVarKey"
                    placeholder={gt('customer_name', {
                      context: 'variable key input placeholder example',
                    })}
                    value={newVarKey}
                    onChange={(e) => setNewVarKey(e.target.value)}
                    className="h-8 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addVariable();
                      }
                    }}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label htmlFor="newVarLabel" className="text-xs">
                    <T>Label (optional)</T>
                  </Label>
                  <Input
                    id="newVarLabel"
                    placeholder={gt('Customer Name', {
                      context: 'variable label input placeholder example',
                    })}
                    value={newVarLabel}
                    onChange={(e) => setNewVarLabel(e.target.value)}
                    className="h-8 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addVariable();
                      }
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={addVariable}
                  disabled={!newVarKey.trim()}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            {errors.variable && (
              <p className="text-sm text-destructive">{errors.variable}</p>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="templateSubject">
            <T>Subject Line</T>
          </Label>
          <Input
            id="templateSubject"
            placeholder={gt(
              'e.g., Action Required: {variable} rebate submission',
              {
                context: 'subject line input placeholder',
                variable: '{{customer_name}}',
              },
            )}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={readOnly}
          />
          {errors.subject && (
            <p className="text-sm text-destructive">{errors.subject}</p>
          )}
        </div>

        {isJsonRender ? (
          <div
            className={cn(
              expandedJsonEditor
                ? 'flex min-h-0 flex-1 flex-col gap-2'
                : 'space-y-2',
            )}
          >
            <Label>
              <T context="json render email template body label">
                Template Blocks
              </T>
            </Label>
            <JsonTemplateEditor
              jsonSpec={jsonSpec}
              onJsonSpecChange={readOnly ? () => {} : setJsonSpec}
              sampleVariables={sampleVariables}
              onSampleVariablesChange={setSampleVariables}
              expanded={expandedJsonEditor}
            />
            {errors.jsonSpec && (
              <p className="text-sm text-destructive">{errors.jsonSpec}</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Label>
              <T>Body</T>
            </Label>
            <div
              className={cn(
                'rounded-md',
                undefinedVars.length > 0 && 'ring-1 ring-destructive',
              )}
            >
              <HtmlWYSIWYGEditor
                initialHtml={body}
                editable={!readOnly}
                onChange={readOnly ? () => {} : handleBodyChange}
                placeholder={gt(
                  'Enter your email template content here. Use {variable} for dynamic values.',
                  {
                    context: 'email body editor placeholder',
                    variable: '{{variable_name}}',
                  },
                )}
                height={200}
              />
            </div>
            {errors.body && (
              <p className="text-sm text-destructive">{errors.body}</p>
            )}
            {undefinedVars.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-sm text-destructive">
                <span>
                  <T>Undefined variables:</T>
                </span>
                {undefinedVars.map((key) => (
                  <Badge
                    key={key}
                    variant="outline"
                    className="border-destructive text-destructive font-mono text-xs"
                  >
                    {`{{${key}}}`}
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground ml-1">
                  <T>— add them above or remove from template</T>
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              <T>
                Click a variable badge above to copy it, then paste into the
                editor
              </T>
            </p>
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            <T>Cancel</T>
          </Button>
          {isEditMode && template ? (
            <>
              <Button
                variant="outline"
                onClick={() => requestSave('draft')}
                disabled={isPending}
              >
                {createDraft.isPending || updateDraft.isPending ? (
                  <T>Saving...</T>
                ) : (
                  <T>Save Draft</T>
                )}
              </Button>
              <Button
                onClick={() => requestSave('publish')}
                disabled={isPending}
              >
                <RocketLaunchIcon className="mr-1.5 h-4 w-4" />
                {finalizeDraft.isPending || publishVersion.isPending ? (
                  <T>Publishing...</T>
                ) : (
                  <T>Save & Publish</T>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? <T>Creating...</T> : <T>Create</T>}
            </Button>
          )}
        </div>
      )}

      <AlertDialog
        open={pendingSaveKind !== null}
        onOpenChange={(open) => !open && setPendingSaveKind(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="email template type-switch confirm title">
                Change template type?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {templateType === 'html' ? (
                <T context="confirm switching an email template to HTML">
                  Saving switches this template to HTML. Its Template Blocks
                  (JSON) content won't be included in the saved version. You can
                  still revert to an earlier version from the version history.
                </T>
              ) : (
                <T context="confirm switching an email template to Template Blocks">
                  Saving switches this template to Template Blocks. Its HTML
                  body won't be included in the saved version. You can still
                  revert to an earlier version from the version history.
                </T>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingSave}>
              <T context="confirm switch and save email template">
                Switch & Save
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
